import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getDomainFormInfo,
  postDomainFormInfo,
} from "@/api/onboarding";

/** @param {string | undefined | null} domainProductInstanceId @param {string | undefined | null} siteplusProductInstanceId */
export const getDomainFormInfoQueryKey = (
  domainProductInstanceId,
  siteplusProductInstanceId,
) => [
  "domainFormInfo",
  domainProductInstanceId,
  siteplusProductInstanceId,
];

/**
 * SFAM domain form: GET via react-query, POST via useMutation.
 *
 * @param {string | undefined | null} domainProductInstanceId — SFcore domain `prodInstId` (URL path)
 * @param {string | undefined | null} siteplusProductInstanceId — `_sp_productInstanceId` for {@link getSiteplusToken} only
 * @param {object} [options]
 * @param {object} [options.query] — extra `useQuery` options (merged after defaults).
 * @param {object} [options.mutation] — extra `useMutation` options; `mutationFn` / `onSuccess` are composed with the defaults.
 */
export const useDomainFormInfo = (
  domainProductInstanceId,
  siteplusProductInstanceId,
  options = {},
) => {
  const { query: queryOptions = {}, mutation: mutationOptions = {} } = options;
  const queryClient = useQueryClient();
  const { onSuccess: userMutationOnSuccess, ...restMutationOptions } =
    mutationOptions;

  const queryEnabled =
    Boolean(domainProductInstanceId) && Boolean(siteplusProductInstanceId);

  const query = useQuery({
    queryKey: getDomainFormInfoQueryKey(
      domainProductInstanceId,
      siteplusProductInstanceId,
    ),
    queryFn: () =>
      getDomainFormInfo(domainProductInstanceId, siteplusProductInstanceId),
    enabled: queryEnabled,
    ...queryOptions,
  });

  const mutation = useMutation({
    ...restMutationOptions,
    mutationFn: (payload) => {
      if (!domainProductInstanceId) {
        throw new Error(
          "useDomainFormInfo: domainProductInstanceId is required to submit the form.",
        );
      }
      if (!siteplusProductInstanceId) {
        throw new Error(
          "useDomainFormInfo: siteplusProductInstanceId is required to submit the form.",
        );
      }
      return postDomainFormInfo(
        domainProductInstanceId,
        payload,
        siteplusProductInstanceId,
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: getDomainFormInfoQueryKey(
          domainProductInstanceId,
          siteplusProductInstanceId,
        ),
      });
      userMutationOnSuccess?.(data, variables, context);
    },
  });

  return { query, mutation };
};
